import '@/styles/globals.css';

import App, { AppContext, AppProps } from 'next/app';

import { SessionProvider } from 'next-auth/react';
import { HeroUIProvider } from '@heroui/system';
import { Provider } from 'react-redux';
import { store } from '@/_shared/store/store';
import Scaffold from '@/components/scaffold/scaffold.component';
import { SidebarProvider } from '@/_shared/context/SidebarContext';
import { DigitalShelfFiltersProvider } from '@/_shared/context/DigitalShelfFiltersContext';
import { UserImageProvider } from '@/_shared/context/UserImageContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/_shared/helpers/react-query.helper';
import { useRouter } from 'next/router';
import PublicScaffold from '@/components/scaffold/public-scaffold.component';
import PrelineScriptWrapper from '@/components/PrelineScriptWrapper';
import AnalyticsConsentManager from '@/components/analytics/analytics-consent-manager.component';

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const isPublic = router.pathname === '/';
  const hideScaffold = (Component as any).hideScaffold ?? false;

  // const isProd = process.env.NODE_ENV === 'production';
  const GA_ID =
    process.env.NEXT_PUBLIC_GA_ID ||
    process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ||
    '';
  const CLARITY_ID = process.env.NEXT_PUBLIC_CLARITY_ID || '';

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <SessionProvider session={pageProps.session}>
            <AnalyticsConsentManager gaId={GA_ID} clarityId={CLARITY_ID} />
            <UserImageProvider>
              <DigitalShelfFiltersProvider>
                <PrelineScriptWrapper />
                <div>
                  {isPublic && (
                    <PublicScaffold>
                      <Component {...pageProps} />
                    </PublicScaffold>
                  )}
                  {hideScaffold && !isPublic && <Component {...pageProps} />}
                  {!hideScaffold && !isPublic && (
                    <SidebarProvider>
                      <HeroUIProvider locale="en-GB">
                        <Scaffold>
                          <Component {...pageProps} />
                        </Scaffold>
                      </HeroUIProvider>
                    </SidebarProvider>
                  )}
                </div>
              </DigitalShelfFiltersProvider>
            </UserImageProvider>
          </SessionProvider>
        </Provider>
      </QueryClientProvider>
    </>
  );
}

// Forces every page through per-request SSR instead of Automatic Static
// Optimization. ASO prerenders pages with `ctx.req` undefined (see
// _document.tsx), which permanently bakes an empty CSP nonce into the HTML
// while middleware (src/proxy.ts) issues a fresh nonce per request -- the two
// can never match, and CSP silently blocks inline scripts (e.g. Turbopack's
// bootstrap script) on every statically-optimized page.
MyApp.getInitialProps = async (appContext: AppContext) => {
  return App.getInitialProps(appContext);
};

export default MyApp;
